import React, { useEffect, useMemo, useRef, useState } from 'react';
import MoreMenu, { ColorPicker } from './MoreMenu';
import useReorder from './useReorder';
import { Icon } from './icons';
import { useConfirm } from '../ConfirmDialog';
import {
  blankStep, canIndent, canOutdent, createTemplate, errorText, indent, levelsOf, moveStep, offsetLabel,
  outdent, removeStep, saveTemplate, subtreeEnd, toBody
} from '../../lib/todoTemplates';

/** The icons a template may carry: the server's list (lib/todo-templates.js ICONS). */
export const TEMPLATE_ICONS = [
  ['template', 'Template'], ['calendar', 'Calendar'], ['receipt', 'Receipt'], ['shield', 'Shield'],
  ['audit', 'Audit'], ['briefcase', 'Briefcase'], ['list', 'List'], ['flag', 'Flag'], ['person', 'Person'],
  ['note', 'Note'], ['clock', 'Clock'], ['repeat', 'Repeat'], ['done', 'Tick'], ['ticket', 'Ticket']
];

const NEW_TEMPLATE = () => ({
  id: null, name: '', description: '', color: 'blue', icon: 'template', visibility: 'personal',
  anchor: 'start', day_kind: 'working', roles: ['Preparer', 'Reviewer'], steps: [blankStep()], can_edit: true
});

/** A choice of two or three, as one row of buttons (the List | Board switch's look). */
function Segmented({ label, value, options, onChange, disabled }) {
  return (
    <span className="tdb-switch tpl-seg" role="group" aria-label={label}>
      {options.map(([v, words]) => (
        <button key={v} type="button" aria-pressed={value === v} disabled={disabled}
                onClick={() => onChange(v)}>{words}</button>
      ))}
    </span>
  );
}

/** Labels as chips: type one and Enter (or a comma) to add it. */
function LabelChips({ value, onChange, known, id, disabled }) {
  const [draft, setDraft] = useState('');
  const add = (raw) => {
    const name = raw.replace(/^@/, '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40);
    setDraft('');
    if (!name || value.some((l) => l.toLowerCase() === name.toLowerCase())) return;
    onChange([...value, name].slice(0, 10));
  };
  return (
    <div className="tpl-chips">
      {value.map((l) => (
        <span key={l} className="tpl-chip">@{l}
          {!disabled && (
            <button type="button" aria-label={`Take the label ${l} off`} onClick={() => onChange(value.filter((x) => x !== l))}>
              <Icon name="close" size={12} />
            </button>
          )}
        </span>
      ))}
      {!disabled && value.length < 10 && (
        <input id={id} type="text" value={draft} list={`${id}-known`} placeholder="Add a label"
               aria-label="Add a label" maxLength={40}
               onChange={(e) => { if (e.target.value.endsWith(',')) add(e.target.value); else setDraft(e.target.value); }}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(draft); } }}
               onBlur={() => { if (draft.trim()) add(draft); }} />
      )}
      <datalist id={`${id}-known`}>{known.map((n) => <option key={n} value={n} />)}</datalist>
    </div>
  );
}

/**
 * THE TEMPLATE EDITOR (071). In place of the gallery, until it is saved or
 * left.
 *
 * Above: what the template is - name, description, colour and icon, who may
 * use it, and how its days are counted (from a start date or back from a
 * due date; working days or calendar days). Then its ROLES, the parts people
 * play in it, named here and given to people when it is used.
 *
 * Then the STEPS, an outline: each step's title, when it falls (so many days
 * before or after the date it is used with, or no date), a time if it has
 * one, its role and its priority, all in the row; its notes, labels,
 * checklist, section and what it waits for behind "Details". Dragged by its
 * ⠿ (useReorder, with its sub-steps coming along), indented under the step
 * above, outdented back. From the keyboard: Enter adds a step below,
 * Ctrl+] and Ctrl+[ indent and outdent, Alt+↑ and Alt+↓ move it, and
 * Backspace in an empty title takes the step away - an outliner's keys.
 *
 * A template somebody may only use (one of the firm's own, for anybody but
 * an administrator) opens read-only, saying so.
 */
export default function TemplateEditor({ template, note = null, isAdmin, labels, phone, onCancel, onSaved }) {
  const [t, setT] = useState(() => {
    const base = template ? { ...template } : NEW_TEMPLATE();
    return { ...base, description: base.description || '', steps: (base.steps || []).map((s) => ({ ...s })) };
  });
  const isNew = !t.id;
  const [confirm, confirmDialog] = useConfirm();
  const readOnly = !isNew && t.can_edit === false;
  const [open, setOpen] = useState(() => new Set());   // steps with Details open
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const listRef = useRef(null);

  const change = (patch) => { setT((prev) => ({ ...prev, ...patch })); setDirty(true); };
  const setSteps = (fn) => { setT((prev) => ({ ...prev, steps: fn(prev.steps) })); setDirty(true); };
  const patchStep = (id, patch) => setSteps((steps) => steps.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const levels = useMemo(() => levelsOf(t.steps), [t.steps]);
  const reorder = useReorder((group, from, to) => {
    if (group === 'steps') setSteps((steps) => moveStep(steps, from, to));
  });

  // A step just added, moved or indented keeps the keyboard.
  useEffect(() => {
    if (!focusId || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-step-title="${focusId}"]`);
    if (el) el.focus();
    setFocusId(null);
  }, [focusId, t.steps]);

  // Leaving with unsaved changes asks first - the browser's own question.
  useEffect(() => {
    if (!dirty || readOnly) return undefined;
    const ask = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [dirty, readOnly]);

  const knownLabels = useMemo(() => [...new Set([...(labels || []).map((l) => l.name),
    ...t.steps.flatMap((s) => s.labels || [])])].sort((a, b) => a.localeCompare(b)), [labels, t.steps]);
  const knownSections = useMemo(() => [...new Set(t.steps.map((s) => s.section).filter(Boolean))], [t.steps]);

  /* ------------------------------------------------------------- roles */

  const renameRole = (i, name) => {
    const old = t.roles[i];
    const roles = t.roles.map((r, k) => (k === i ? name : r));
    change({ roles, steps: t.steps.map((s) => (s.role && s.role === old ? { ...s, role: name } : s)) });
  };
  const removeRole = (i) => {
    const old = t.roles[i];
    change({ roles: t.roles.filter((_, k) => k !== i),
      steps: t.steps.map((s) => (s.role === old ? { ...s, role: null } : s)) });
  };
  const addRole = () => {
    let n = t.roles.length + 1;
    while (t.roles.includes(`Role ${n}`)) n += 1;
    change({ roles: [...t.roles, `Role ${n}`] });
    setTimeout(() => {
      const all = document.querySelectorAll('.tpl-role-input');
      if (all.length) { all[all.length - 1].focus(); all[all.length - 1].select(); }
    }, 0);
  };

  /* ------------------------------------------------------------- steps */

  const addAfter = (i) => {
    const s = i >= 0 ? t.steps[i] : null;
    const step = blankStep({
      parent: s ? s.parent : null,
      section: s && !s.parent ? s.section : null,
      offset: s ? s.offset : (t.anchor === 'due' ? 0 : 0),
      role: s ? s.role : null
    });
    const at = i >= 0 ? subtreeEnd(t.steps, i) : t.steps.length;
    setSteps((steps) => [...steps.slice(0, at), step, ...steps.slice(at)]);
    setFocusId(step.id);
  };
  const addSub = (i) => {
    const s = t.steps[i];
    if (levels.get(s.id) >= 2) return;
    const step = blankStep({ parent: s.id, offset: s.offset, role: s.role });
    const at = subtreeEnd(t.steps, i);
    setSteps((steps) => [...steps.slice(0, at), step, ...steps.slice(at)]);
    setFocusId(step.id);
  };
  const duplicateStep = (i) => {
    const s = t.steps[i];
    const copy = { ...blankStep(), ...s, id: blankStep().id, title: `${s.title} (copy)`.slice(0, 300), after: [...(s.after || [])] };
    const at = subtreeEnd(t.steps, i);
    setSteps((steps) => [...steps.slice(0, at), copy, ...steps.slice(at)]);
    setFocusId(copy.id);
  };
  const remove = (i) => {
    const prev = t.steps[i - 1] || t.steps[i + 1];
    setSteps((steps) => removeStep(steps, i));
    if (prev) setFocusId(prev.id);
  };
  const move = (i, dir) => {
    // One sibling up or down: past the whole subtree of the step it passes.
    const lv = levelsOf(t.steps);
    if (dir < 0) {
      let j = i - 1;
      while (j >= 0 && lv.get(t.steps[j].id) > lv.get(t.steps[i].id)) j -= 1;
      if (j < 0) return;
      setSteps((steps) => moveStep(steps, i, j));
    } else {
      const end = subtreeEnd(t.steps, i);
      if (end >= t.steps.length) return;
      const nextEnd = subtreeEnd(t.steps, end);
      setSteps((steps) => moveStep(steps, i, nextEnd - 1));
    }
    setFocusId(t.steps[i].id);
  };
  const doIndent = (i) => { setSteps((steps) => indent(steps, i)); setFocusId(t.steps[i].id); };
  const doOutdent = (i) => { setSteps((steps) => outdent(steps, i)); setFocusId(t.steps[i].id); };

  const onTitleKey = (e, i) => {
    const s = t.steps[i];
    if (e.key === 'Enter') { e.preventDefault(); addAfter(i); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === ']') { e.preventDefault(); doIndent(i); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === '[') { e.preventDefault(); doOutdent(i); return; }
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) { e.preventDefault(); move(i, e.key === 'ArrowUp' ? -1 : 1); return; }
    if (e.key === 'Backspace' && !s.title && t.steps.length > 1 && subtreeEnd(t.steps, i) === i + 1) {
      e.preventDefault();
      remove(i);
    }
  };

  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /* -------------------------------------------------------------- save */

  const save = async () => {
    setError(null);
    const name = t.name.trim();
    if (!name) { setError('Give the template a name'); document.getElementById('tpl-name')?.focus(); return; }
    const blank = t.steps.findIndex((s) => !String(s.title || '').trim());
    if (blank >= 0) {
      setError(`Step ${blank + 1} needs a title`);
      setFocusId(t.steps[blank].id);
      return;
    }
    const roles = t.roles.map((r) => r.trim()).filter(Boolean);
    const body = toBody({ ...t, name, roles, steps: t.steps.map((s) => ({ ...s, role: s.role && roles.includes(s.role.trim()) ? s.role.trim() : null })) });
    setBusy(true);
    try {
      const saved = isNew ? await createTemplate(body) : await saveTemplate(t.id, body);
      setDirty(false);
      onSaved(saved, isNew);
    } catch (err) {
      setError(errorText(err, 'Could not save the template'));
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (dirty && !readOnly
      && !(await confirm({ title: 'Leave without saving your changes to this template?', confirmLabel: 'Leave', danger: true }))) return;
    onCancel();
  };

  /* ------------------------------------------------------------- a step */

  const when = (s) => (s.offset === null || s.offset === undefined || s.offset === '' ? 'none'
    : Number(s.offset) < 0 ? 'before' : Number(s.offset) > 0 ? 'after' : (t.anchor === 'due' ? 'before' : 'after'));

  const setWhen = (s, dir, n) => {
    if (dir === 'none') { patchStep(s.id, { offset: null, time: null }); return; }
    const abs = Math.max(0, Math.min(3650, Math.abs(Number.isFinite(n) ? Math.trunc(n) : 0)));
    patchStep(s.id, { offset: dir === 'before' ? -abs : abs });
  };

  const stepRow = (s, i) => {
    const lv = levels.get(s.id) || 0;
    const isOpen = open.has(s.id);
    const dir = when(s);
    const abs = s.offset === null || s.offset === undefined ? 0 : Math.abs(Number(s.offset));
    const others = t.steps.filter((x) => x.id !== s.id);
    const extras = [
      s.notes && 'notes',
      (s.labels || []).length && `${s.labels.length} label${s.labels.length === 1 ? '' : 's'}`,
      (s.checklist || []).length && `${s.checklist.length}-line checklist`,
      (s.after || []).length && `waits for ${s.after.length}`
    ].filter(Boolean);
    const num = i + 1;
    const top = !s.parent;
    const prevTop = [...t.steps.slice(0, i)].reverse().find((x) => !x.parent);
    const sectionMark = top && s.section && (!prevTop || prevTop.section !== s.section);
    return (
      <React.Fragment key={s.id}>
        {sectionMark && <li className="tpl-sec-mark" aria-hidden="true">{s.section}</li>}
        <li className={`tpl-step${reorder.markOf('steps', i, t.steps.length)}${isOpen ? ' is-open' : ''}`}
            data-reorder-item="" data-step={s.id} data-level={lv} style={{ '--tpl-level': lv }}>
          <div className="tpl-step-row">
            {readOnly ? <span className="tpl-grip-gap" aria-hidden="true" /> : (
              <span className="tda-grip tpl-grip" aria-hidden="true" title="Drag to reorder"
                    onPointerDown={(e) => reorder.start(e, 'steps', i)}><Icon name="grip" size={14} /></span>
            )}
            <span className={`tpl-step-pri is-p${s.priority || 4}`} aria-hidden="true" />
            <input type="text" className="tpl-step-title" value={s.title} maxLength={300}
                   data-step-title={s.id} placeholder={lv ? 'Sub-step' : 'What needs doing'}
                   aria-label={`Step ${num}: title`} readOnly={readOnly}
                   onChange={(e) => patchStep(s.id, { title: e.target.value })}
                   onKeyDown={(e) => { if (!readOnly) onTitleKey(e, i); }} />
            <span className="tpl-step-when" role="group" aria-label={`Step ${num}: when`}>
              {dir !== 'none' && (
                <input type="number" min="0" max="3650" inputMode="numeric" value={abs} disabled={readOnly}
                       aria-label={`Step ${num}: how many ${t.day_kind === 'working' ? 'working ' : ''}days`}
                       onChange={(e) => setWhen(s, dir, Number(e.target.value))} />
              )}
              <select value={dir} disabled={readOnly} aria-label={`Step ${num}: before or after`}
                      onChange={(e) => setWhen(s, e.target.value, abs)}>
                <option value="before">{t.day_kind === 'working' ? 'working days' : 'days'} before</option>
                <option value="after">{t.day_kind === 'working' ? 'working days' : 'days'} after</option>
                <option value="none">No date</option>
              </select>
            </span>
            <input type="time" className="tpl-step-time" value={s.time || ''} disabled={readOnly || dir === 'none'}
                   aria-label={`Step ${num}: time (optional)`}
                   onChange={(e) => patchStep(s.id, { time: e.target.value || null })} />
            <select className="tpl-step-role" value={s.role || ''} disabled={readOnly} aria-label={`Step ${num}: role`}
                    onChange={(e) => patchStep(s.id, { role: e.target.value || null })}>
              <option value="">Anyone</option>
              {t.roles.filter((r) => r.trim()).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="tpl-step-prio" value={s.priority || 4} disabled={readOnly} aria-label={`Step ${num}: priority`}
                    onChange={(e) => patchStep(s.id, { priority: Number(e.target.value) })}>
              {[1, 2, 3, 4].map((p) => <option key={p} value={p}>P{p}</option>)}
            </select>
            <button type="button" className={`tpl-details-btn${extras.length ? ' has-some' : ''}`}
                    aria-expanded={isOpen} aria-controls={`tpl-d-${s.id}`}
                    aria-label={`Step ${num}: details${extras.length ? ` (${extras.join(', ')})` : ''}`}
                    title="Notes, labels, checklist, section, waits for" onClick={() => toggle(s.id)}>
              <Icon name="chevronDown" size={14} />
              {extras.length > 0 && <span className="tpl-details-n">{extras.length}</span>}
            </button>
            {!readOnly && (
              <MoreMenu label={`More for step ${num}`} items={[
                { label: 'Add a step below', icon: 'plus', hint: '↵', onClick: () => addAfter(i) },
                lv < 2 && { label: 'Add a sub-step', icon: 'plus', onClick: () => addSub(i) },
                { sep: true },
                canIndent(t.steps, i) && { label: 'Indent', icon: 'indent', hint: 'Ctrl ]', onClick: () => doIndent(i) },
                canOutdent(t.steps, i) && { label: 'Outdent', icon: 'outdent', hint: 'Ctrl [', onClick: () => doOutdent(i) },
                i > 0 && { label: 'Move up', icon: 'arrowUp', hint: 'Alt ↑', onClick: () => move(i, -1) },
                subtreeEnd(t.steps, i) < t.steps.length && { label: 'Move down', icon: 'arrowDown', hint: 'Alt ↓', onClick: () => move(i, 1) },
                { sep: true },
                { label: 'Duplicate step', icon: 'copy', onClick: () => duplicateStep(i) },
                t.steps.length > 1 && { label: 'Delete step', icon: 'trash', danger: true, onClick: () => remove(i) }
              ]} />
            )}
          </div>
          <p className="tpl-step-sub">
            <span className="tpl-step-offset">{offsetLabel(s.offset, t)}{s.time && dir !== 'none' ? ` at ${s.time}` : ''}</span>
            {top && s.section && <span className="tpl-step-tag"><Icon name="list" size={12} />{s.section}</span>}
            {extras.map((x) => <span key={x} className="tpl-step-tag">{x}</span>)}
          </p>
          {isOpen && (
            <div className="tpl-details" id={`tpl-d-${s.id}`}>
              <label className="tds-field tpl-details-notes">
                <span>Notes</span>
                <textarea rows={2} maxLength={4000} value={s.notes || ''} readOnly={readOnly}
                          onChange={(e) => patchStep(s.id, { notes: e.target.value })} />
              </label>
              <div className="tds-field">
                <span id={`tpl-l-${s.id}-h`}>Labels</span>
                <LabelChips id={`tpl-l-${s.id}`} value={s.labels || []} known={knownLabels} disabled={readOnly}
                            onChange={(v) => patchStep(s.id, { labels: v })} />
              </div>
              <label className="tds-field">
                <span>Checklist - one line each</span>
                <textarea rows={Math.min(6, Math.max(2, (s.checklist || []).length + 1))} readOnly={readOnly}
                          value={(s.checklist || []).join('\n')}
                          onChange={(e) => patchStep(s.id, { checklist: e.target.value.split('\n').map((x) => x.replace(/^\s*[-*•]\s*/, '')).slice(0, 100) })}
                          onBlur={(e) => patchStep(s.id, { checklist: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} />
              </label>
              {top ? (
                <label className="tds-field">
                  <span>Section</span>
                  <input type="text" maxLength={80} value={s.section || ''} list="tpl-sections" readOnly={readOnly}
                         placeholder="None" onChange={(e) => patchStep(s.id, { section: e.target.value || null })} />
                </label>
              ) : <p className="tpl-quiet tpl-details-sec">A sub-step goes in its step’s section.</p>}
              {others.length > 0 && (
                <fieldset className="tpl-after" disabled={readOnly}>
                  <legend>Waits for</legend>
                  <ul>
                    {others.map((o) => (
                      <li key={o.id}>
                        <label>
                          <input type="checkbox" checked={(s.after || []).includes(o.id)}
                                 onChange={(e) => patchStep(s.id, { after: e.target.checked
                                   ? [...(s.after || []), o.id] : (s.after || []).filter((a) => a !== o.id) })} />
                          <span>{t.steps.indexOf(o) + 1}. {o.title || 'Untitled step'}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </fieldset>
              )}
            </div>
          )}
        </li>
      </React.Fragment>
    );
  };

  return (
    <div className="tpl tpl-edit">
      <div className="tda-head tpl-head">
        <button type="button" className="tda-add-line tpl-back" onClick={cancel}>← Templates</button>
        <h2 className="tda-title">
          <span className="tda-title-text">{readOnly ? t.name : isNew ? 'New template' : 'Edit template'}</span>
        </h2>
        {!readOnly && (
          <span className="tpl-head-btns">
            <button type="button" className="btn btn-outline btn-small" onClick={cancel} disabled={busy}>Cancel</button>
            <button type="button" className="btn btn-primary btn-small tpl-save" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save template'}
            </button>
          </span>
        )}
      </div>

      {note && <p className="tpl-note" role="status">{note}</p>}
      {readOnly && (
        <p className="tpl-note" role="status">
          {t.system
            ? 'This is one of the firm’s own templates: an administrator can change it. Duplicate it from its ⋯ to make one of your own.'
            : `${t.owner_name || 'Its owner'} made this template, so they (or an administrator) can change it. Duplicate it from its ⋯ to make one of your own.`}
        </p>
      )}
      {error && <p className="ctd-error" role="alert">{error}</p>}

      <fieldset className="tpl-form" disabled={readOnly}>
        <legend className="tpl-visually-hidden">About the template</legend>
        <div className="tpl-form-grid">
          <label className="tds-field tpl-f-name">
            <span>Name</span>
            <input id="tpl-name" type="text" value={t.name} maxLength={80} placeholder="VAT return"
                   onChange={(e) => change({ name: e.target.value })} />
          </label>
          <label className="tds-field tpl-f-desc">
            <span>Description</span>
            <textarea rows={2} maxLength={1000} value={t.description || ''}
                      placeholder="What it is for, and the date to use it with"
                      onChange={(e) => change({ description: e.target.value })} />
          </label>
          <div className="tds-field tpl-f-look">
            <span>Colour and icon</span>
            <div className="tpl-look">
              <ColorPicker name={t.name || 'the template'} value={t.color} onPick={(c) => change({ color: c })} />
              <div className="tpl-icons" role="radiogroup" aria-label="Icon">
                {TEMPLATE_ICONS.map(([name, words]) => (
                  <button key={name} type="button" role="radio" aria-checked={t.icon === name} title={words}
                          aria-label={`Icon: ${words}`} className="tpl-icon-btn" onClick={() => change({ icon: name })}>
                    <Icon name={name} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="tds-field tpl-f-who">
            <span>Who can use it</span>
            <Segmented label="Who can use it" value={t.visibility} disabled={readOnly || (t.system && !isNew)}
                       options={[['firm', 'Everybody at NCMB'], ['personal', 'Just me']]}
                       onChange={(v) => change({ visibility: v })} />
          </div>
          <div className="tds-field tpl-f-count">
            <span>Its days count</span>
            <span className="tpl-count">
              <Segmented label="Count from" value={t.anchor} disabled={readOnly}
                         options={[['start', 'From a start date'], ['due', 'Back from a due date']]}
                         onChange={(v) => change({ anchor: v })} />
              <Segmented label="Count in" value={t.day_kind} disabled={readOnly}
                         options={[['working', 'Working days'], ['calendar', 'Calendar days']]}
                         onChange={(v) => change({ day_kind: v })} />
            </span>
            <small className="tpl-hint">
              {t.day_kind === 'working'
                ? 'Working days skip weekends and Malta’s public holidays, Good Friday included.'
                : 'Calendar days count every day, weekends and holidays too.'}
              {t.anchor === 'due' ? ' A due date that is not a working day counts from the working day before.' : ''}
            </small>
          </div>
        </div>

        <div className="tpl-roles-edit">
          <h3 className="tpl-sub-head">Roles</h3>
          <p className="tpl-quiet">The parts people play. Each step goes to a role, and you say who plays it when you use the template.</p>
          <ul className="tpl-role-list" aria-label="Roles">
            {t.roles.map((r, i) => (
              // Keyed by place: renaming a role must not remount its field.
              <li key={i} className="tpl-role-edit">
                <Icon name="person" size={14} />
                <input type="text" className="tpl-role-input" value={r} maxLength={40} aria-label={`Role ${i + 1}`}
                       onChange={(e) => renameRole(i, e.target.value)} />
                {!readOnly && (
                  <button type="button" className="tds-icon-btn is-danger" aria-label={`Remove the role ${r || i + 1}`}
                          title="Remove" onClick={() => removeRole(i)}><Icon name="close" size={14} /></button>
                )}
              </li>
            ))}
            {!readOnly && t.roles.length < 12 && (
              <li><button type="button" className="tda-add-line" onClick={addRole}>+ Add role</button></li>
            )}
          </ul>
        </div>
      </fieldset>

      <section className="tpl-steps" aria-label="Steps">
        <div className="tpl-steps-head">
          <h3 className="tpl-sub-head">Steps<span className="tpl-group-n">{t.steps.length}</span></h3>
          {!readOnly && !phone && (
            <span className="tpl-keys">Enter adds a step · Ctrl ] indents · Ctrl [ outdents · Alt ↑↓ moves</span>
          )}
        </div>
        <datalist id="tpl-sections">{knownSections.map((n) => <option key={n} value={n} />)}</datalist>
        <ol className="tpl-step-list" data-reorder="steps" ref={listRef}>
          {t.steps.map(stepRow)}
        </ol>
        {!readOnly && (
          <button type="button" className="tda-add-line tpl-add-step" onClick={() => addAfter(t.steps.length - 1)}>
            + Add step
          </button>
        )}
      </section>

      {!readOnly && (
        <div className="tpl-foot">
          <button type="button" className="btn btn-outline" onClick={cancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save template'}
          </button>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
